import { CaptureUpdateAction, useExcalidrawAPI } from "@excalidraw/excalidraw";
import {
  frameToolIcon,
  TrashIcon,
} from "@excalidraw/excalidraw/components/icons";
import { useUIAppState } from "@excalidraw/excalidraw/context/ui-appState";
import {
  duplicateElements,
  getElementsOverlappingFrame,
  newFrameElement,
  syncInvalidIndices,
} from "@excalidraw/element";

import { useEffect, useReducer, useRef, useState } from "react";

import {
  arrayToMap,
  SCREEN_SIZE_PRESETS,
  findPresetIndex,
} from "@excalidraw/common";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import { getScreens } from "../flipbook/ops";

import "./ScreenMenu.scss";

/**
 * Top-left bar dropdown that manages "screens" (frame elements): list & jump to
 * screens, create blank/duplicate screens, delete a screen (frame + its member
 * elements), and size the active screen — social-media presets (scroll the
 * preview to cycle) or a custom W×H. Replaces the former right-sidebar
 * "Screens" tab.
 */
export const ScreenMenu = () => {
  const excalidrawAPI = useExcalidrawAPI();
  const appState = useUIAppState();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  // component re-renders on appState changes only; while the popover is open we
  // also need scene-element changes (create/delete screens) reflected live, so
  // subscribe to onChange and force a render.
  const [, forceRender] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    if (!open || !excalidrawAPI) {
      return;
    }
    return excalidrawAPI.onChange(() => forceRender());
  }, [open, excalidrawAPI]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const elements = excalidrawAPI?.getSceneElements() ?? [];
  const frames = getScreens(elements);

  // active screen = the one holding a selected element, else the last screen
  let activeFrame = frames[frames.length - 1];
  for (const frame of frames) {
    if (appState.selectedElementIds[frame.id]) {
      activeFrame = frame;
    }
  }

  const goToScreen = (frame: typeof frames[number]) => {
    excalidrawAPI?.setViewport({
      target: [frame],
      fit: "scale-down",
      animation: true,
    });
  };

  // resize the active screen (top-left anchored) and re-fit the view
  const applyScreenSize = (width: number, height: number) => {
    if (!activeFrame) {
      return;
    }
    excalidrawAPI?.mutateElement(activeFrame as ExcalidrawElement, {
      width: Math.max(1, Math.round(width)),
      height: Math.max(1, Math.round(height)),
    });
    // mutateElement alone never records an undo entry
    excalidrawAPI?.updateScene({
      elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });
    goToScreen(activeFrame);
  };

  // scroll over the size preview to step through presets, live-resizing
  const cyclePreset = (direction: 1 | -1) => {
    if (!activeFrame) {
      return;
    }
    const current = findPresetIndex(activeFrame.width, activeFrame.height);
    const start = current === -1 ? (direction === 1 ? -1 : 0) : current;
    const next =
      (start + direction + SCREEN_SIZE_PRESETS.length) %
      SCREEN_SIZE_PRESETS.length;
    applyScreenSize(
      SCREEN_SIZE_PRESETS[next].width,
      SCREEN_SIZE_PRESETS[next].height,
    );
  };

  // remove a screen: the frame element plus the elements assigned to it
  const deleteScreen = (frameId: string) => {
    if (!excalidrawAPI) {
      return;
    }
    const remaining = excalidrawAPI
      .getSceneElements()
      .filter((el) => el.id !== frameId && el.frameId !== frameId);
    excalidrawAPI.updateScene({
      elements: remaining,
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });
  };

  const addBlankScreen = () => {
    if (!excalidrawAPI) {
      return;
    }
    setBusy(true);
    try {
      const state = excalidrawAPI.getAppState();
      const lastFrame = frames[frames.length - 1];
      const frame = newFrameElement(
        lastFrame
          ? {
              x: lastFrame.x + lastFrame.width + 80,
              y: lastFrame.y,
              width: lastFrame.width,
              height: lastFrame.height,
              name: `Screen ${frames.length + 1}`,
            }
          : {
              x: -state.scrollX + 80,
              y: -state.scrollY + 80,
              width: 640,
              height: 480,
              name: "Screen 1",
            },
      );
      excalidrawAPI.updateScene({
        elements: [...excalidrawAPI.getSceneElements(), frame],
      });
    } finally {
      setBusy(false);
    }
  };

  const duplicateScreen = () => {
    if (!excalidrawAPI || frames.length === 0) {
      return;
    }
    setBusy(true);
    try {
      const sceneElements = excalidrawAPI.getSceneElements();
      const lastFrame = frames[frames.length - 1];
      const pageElements = [
        lastFrame,
        ...getElementsOverlappingFrame(
          sceneElements,
          lastFrame,
          arrayToMap(sceneElements),
        ),
      ];
      const offsetX = lastFrame.width + 80;
      const { elementsWithDuplicates } = duplicateElements({
        type: "in-place",
        elements: sceneElements,
        idsOfElementsToDuplicate: arrayToMap(pageElements),
        appState: { editingGroupId: null, selectedGroupIds: {} },
        randomizeSeed: false,
        overrides: ({ origElement }) => ({
          x: origElement.x + offsetX,
          ...(origElement.id === lastFrame.id
            ? { name: `Screen ${frames.length + 1}` }
            : {}),
        }),
      });
      excalidrawAPI.updateScene({
        // in-place clones inherit their originals' indices — sync so the
        // strictly-increasing fractional-index invariant holds
        elements: syncInvalidIndices(elementsWithDuplicates),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="screen-menu" ref={rootRef}>
      <button
        type="button"
        className={`screen-menu__trigger ${open ? "is-open" : ""}`}
        title="Screens"
        aria-label="Screens"
        aria-expanded={open}
        data-testid="top-left-bar-screen"
        onClick={() => setOpen((value) => !value)}
      >
        {frameToolIcon}
      </button>

      {open && (
        <div className="screen-menu__popover" role="menu">
          <div className="screen-menu__section">
            <div className="screen-menu__heading">
              Screens ({frames.length})
            </div>
            <div className="screen-menu__list">
              {frames.length === 0 ? (
                <p className="screen-menu__empty">No screens yet</p>
              ) : (
                frames.map((frame, index) => (
                  <div
                    key={frame.id}
                    className={`screen-menu__row ${
                      frame === activeFrame ? "is-active" : ""
                    }`}
                  >
                    <button
                      type="button"
                      className="screen-menu__item"
                      title={frame.name || `Screen ${index + 1}`}
                      onClick={() => goToScreen(frame)}
                    >
                      {frame.name || `Screen ${index + 1}`}
                    </button>
                    <button
                      type="button"
                      className="screen-menu__delete"
                      title="Delete screen"
                      aria-label={`Delete ${
                        frame.name || `Screen ${index + 1}`
                      }`}
                      onClick={() => deleteScreen(frame.id)}
                    >
                      {TrashIcon}
                    </button>
                  </div>
                ))
              )}
            </div>
            <div className="screen-menu__actions">
              <button
                type="button"
                className="screen-menu__btn screen-menu__btn--primary"
                title="Draw a screen on the canvas by dragging (like a frame)"
                onClick={() => {
                  // a "screen" is a frame element — the frame tool gives the
                  // crosshair cursor + drag-to-draw the user expects
                  excalidrawAPI?.setActiveTool({ type: "frame" });
                  setOpen(false);
                }}
                disabled={busy}
              >
                ✎ Draw screen
              </button>
              <button
                type="button"
                className="screen-menu__btn"
                onClick={addBlankScreen}
                disabled={busy}
              >
                {frames.length === 0 ? "+ Create first screen" : "+ New screen"}
              </button>
              {frames.length > 0 && (
                <button
                  type="button"
                  className="screen-menu__btn"
                  onClick={duplicateScreen}
                  disabled={busy}
                >
                  + Duplicate
                </button>
              )}
            </div>
          </div>

          {activeFrame &&
            (() => {
              const w = Math.round(activeFrame.width);
              const h = Math.round(activeFrame.height);
              const presetIndex = findPresetIndex(w, h);
              const scale = w && h ? 44 / Math.max(w, h) : 0;
              return (
                <div className="screen-menu__section">
                  <div className="screen-menu__heading">Size</div>
                  {/* preview — scroll to cycle presets, live-resizing */}
                  <div
                    className="screen-menu__size-window"
                    title="Scroll to change size"
                    onWheel={(event) => {
                      event.preventDefault();
                      cyclePreset(event.deltaY > 0 ? 1 : -1);
                    }}
                  >
                    <div className="screen-menu__preview">
                      <div
                        className="screen-menu__preview-box"
                        style={{
                          width: Math.max(6, w * scale),
                          height: Math.max(6, h * scale),
                        }}
                      />
                    </div>
                    <div className="screen-menu__size-meta">
                      <div className="screen-menu__size-name">
                        {presetIndex === -1
                          ? "Custom"
                          : SCREEN_SIZE_PRESETS[presetIndex].name}
                      </div>
                      <div className="screen-menu__size-dims">
                        {w} × {h}
                      </div>
                    </div>
                  </div>

                  <div className="screen-menu__presets">
                    {SCREEN_SIZE_PRESETS.map((preset, index) => (
                      <button
                        key={preset.name}
                        type="button"
                        className={`screen-menu__chip ${
                          index === presetIndex ? "is-active" : ""
                        }`}
                        title={`${preset.name} — ${preset.width}×${preset.height}`}
                        onClick={() =>
                          applyScreenSize(preset.width, preset.height)
                        }
                      >
                        {preset.name}
                        <span className="screen-menu__chip-ratio">
                          {preset.ratio}
                        </span>
                      </button>
                    ))}
                  </div>

                  <div className="screen-menu__custom">
                    <span>Custom</span>
                    <input
                      type="number"
                      min={1}
                      aria-label="Width"
                      value={w}
                      onChange={(event) =>
                        applyScreenSize(Number(event.target.value), h)
                      }
                    />
                    <span className="screen-menu__times">×</span>
                    <input
                      type="number"
                      min={1}
                      aria-label="Height"
                      value={h}
                      onChange={(event) =>
                        applyScreenSize(w, Number(event.target.value))
                      }
                    />
                  </div>
                </div>
              );
            })()}
        </div>
      )}
    </div>
  );
};
